import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Globe, ChevronDown, Check } from "lucide-react";
import { LANGS } from "./translations";
import { useLang } from "./LanguageContext";

const LANG_NAMES = { pt: "Português", es: "Español", en: "English", it: "Italiano" };

export default function LanguageSwitcher({ dark = false }) {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        data-testid="lang-toggle-btn"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-bold tracking-wide transition-colors ${
          dark ? "border-white/25 text-stone-200 hover:border-white/50" : "border-stone-200 text-stone-600 hover:border-stone-400"
        }`}
      >
        <Globe size={13} className={dark ? "text-stone-300" : "text-stone-400"} />
        {lang.toUpperCase()}
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            className="absolute top-full right-0 pt-2 z-50"
          >
            <div className="bg-white rounded-2xl border border-stone-200/80 shadow-[0_16px_40px_-8px_rgba(0,0,0,0.18)] p-2 min-w-[160px]">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`lang-${l.code}-btn`}
                  onClick={() => {
                    setLang(l.code);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                    lang === l.code ? "bg-[#D9A854]/10 text-[#7A571C]" : "text-stone-700 hover:bg-stone-100"
                  }`}
                >
                  {LANG_NAMES[l.code]}
                  {lang === l.code && <Check size={14} className="text-[#D9A854]" />}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
